/**
 * Order → Messages tab: the conversation between client and builder (order
 * events of kind 'message'), plus the activity list the Overview shows.
 */
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { mktError } from '@/api/marketplace'
import { sendOrderMessage, type TimelineEvent } from '@/api/marketplaceBuild'
import { firstName, fmtDateTime, type OrderCtx } from './orderShared'

export default function OrderMessagesTab(ctx: OrderCtx) {
  const { order, me } = ctx
  const messages = order.events.filter((e) => e.kind === 'message')
  const other = order.role === 'builder' ? order.client.person || order.client.name : firstName(order.builder.person, order.builder.name)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }) }, [messages.length])

  const send = async () => {
    const t = text.trim()
    if (!t) return
    setSending(true)
    try {
      const ev = await sendOrderMessage(order.id, t)
      ctx.setOrder({ ...order, events: [...order.events, ev] })
      setText('')
    } catch (e) {
      toast.error(mktError(e).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="mkt-panel mkt-col" aria-label="Messages" style={{ gap: 14 }}>
      <h2>Messages</h2>
      <div className="mkt-col" style={{ gap: 10, maxHeight: 520, overflowY: 'auto', padding: 2 }} aria-live="polite">
        {messages.length === 0 && <span className="mkt-muted">No messages yet. Questions, files to share, quick changes — write here.</span>}
        {messages.map((m) => <Bubble key={m.id} ev={m} mine={!!me && m.actor_user_id === me} />)}
        <div ref={endRef} />
      </div>
      {order.status === 'declined' || order.status === 'cancelled' ? (
        <span className="mkt-faint">This order has ended, so messages are closed.</span>
      ) : (
        <form className="mkt-col" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); send() }}>
          <div className="mkt-field">
            <label htmlFor="order-msg">Message {other}</label>
            <textarea id="order-msg" rows={3} value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send() } }} />
          </div>
          <div className="mkt-row">
            <button type="submit" className="btn btn-primary" disabled={sending || !text.trim()}>{sending ? 'Sending…' : 'Send'}</button>
            <span className="mkt-faint">Ctrl + Enter sends.</span>
          </div>
        </form>
      )}
    </section>
  )
}

export function Bubble({ ev, mine }: { ev: TimelineEvent; mine: boolean }) {
  return (
    <div style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '80%', display: 'flex', flexDirection: 'column', gap: 2, alignItems: mine ? 'flex-end' : 'flex-start' }}>
      <div style={{
        padding: '9px 13px', borderRadius: 14, whiteSpace: 'pre-line', fontSize: 14,
        background: mine ? 'var(--color-accent)' : 'var(--color-raised, var(--color-bg))',
        color: mine ? '#fff' : 'var(--color-text)',
      }}>{ev.text}</div>
      <span className="mkt-faint">{mine ? 'You' : ev.actor || 'Twasta'} · {fmtDateTime(ev.at)}</span>
    </div>
  )
}

/** "What happened" — the order's events (newest first). */
export function ActivityList({ events, limit }: { events: TimelineEvent[]; limit?: number }) {
  const [all, setAll] = useState(false)
  const rows = events.filter((e) => e.kind === 'event').slice().reverse()
  const shown = all || !limit ? rows : rows.slice(0, limit)
  if (!rows.length) return <span className="mkt-muted">Nothing yet.</span>
  return (
    <div className="mkt-col" style={{ gap: 8 }}>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((e) => (
          <li key={e.id} className="mkt-row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
            <span className="mkt-faint" style={{ flex: '0 0 150px' }}>{fmtDateTime(e.at)}</span>
            <span style={{ fontSize: 14 }}>{e.actor ? <strong>{e.actor}: </strong> : null}{e.text}</span>
          </li>
        ))}
      </ol>
      {limit && rows.length > limit && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setAll(!all)}>
          {all ? 'Show less' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  )
}
